import { get, set } from 'lodash';

import { COMPANY_TYPE, ENTITY_TYPE, FIELD_PATHS } from '@funnels/constants';
import {
  Asset,
  FormValuesPostProductSelect,
  PostProductSelectContract,
  RelatedParty,
  StoreEmployment,
} from '@funnels/types';

import {
  INITIAL_ACCOUNTANT_RELATED_PARTY,
  INITIAL_APPLICANT_POST_PRODUCT_SELECT,
  INITIAL_SOLICITOR_RELATED_PARTY,
} from './initialFormValues';

export function setAccountantAndConveyancerInitialValues(
  formValues: FormValuesPostProductSelect,
  applicationId: string,
  isTeamView?: boolean
) {
  const postProductSelects = get(formValues, FIELD_PATHS.POST_PRODUCT_SELECTS(), []) as PostProductSelectContract[];
  const relatedParties = get(formValues, FIELD_PATHS.RELATED_PARTIES(), []) as RelatedParty[];
  const relatedPartiesToBeCreated: RelatedParty[] = [];
  const employments = get(formValues, FIELD_PATHS.EMPLOYMENTS(), []) as StoreEmployment[];
  const assets = get(formValues, FIELD_PATHS.ASSETS(), []) as Asset[];
  const postProductSelectToBeCreated: PostProductSelectContract[] = [];

  postProductSelects
    .filter(
      (pps) =>
        get({ postProductSelect: [pps] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_TYPE(0)) === ENTITY_TYPE.ACCOUNTANT ||
        get({ postProductSelect: [pps] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_TYPE(0)) === ENTITY_TYPE.SOLICITOR
    )
    .forEach((pps) => {
      const entityId = get({ postProductSelect: [pps] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_ID(0));
      const isAccountantPPS =
        get({ postProductSelect: [pps] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_TYPE(0)) === ENTITY_TYPE.ACCOUNTANT;
      const hasAccountantRelatedParty = relatedParties.some(
        (relatedParty) =>
          get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_EMPLOYMENT_ID(0)) === entityId
      );
      const hasSolicitorRelatedParty = relatedParties.some(
        (relatedParty) =>
          get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) === COMPANY_TYPE.Solicitor
      );

      // initialise related party for the Accountant if it doesn't exists yet
      if (
        isAccountantPPS &&
        !hasAccountantRelatedParty &&
        employments.some((employment) => get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_ID(0)) === entityId)
      ) {
        relatedPartiesToBeCreated.push(INITIAL_ACCOUNTANT_RELATED_PARTY(applicationId, entityId));
      }

      // initialise related party for the Solicitor if it doesn't exists yet
      if (
        !isAccountantPPS &&
        !hasSolicitorRelatedParty &&
        assets.some((asset) => get({ assets: [asset] }, FIELD_PATHS.ASSET_ID(0)) === entityId)
      ) {
        relatedPartiesToBeCreated.push(INITIAL_SOLICITOR_RELATED_PARTY(applicationId, entityId));
      }

      // there has edge case in TV that has Accountant but no Applicant from BE but still need to display the applicant section
      if (isAccountantPPS && isTeamView) {
        const employment = employments.find((e: StoreEmployment) => e.id === entityId);
        const applicantId = get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(0));
        const hasApplicantPPS = [...postProductSelects, ...postProductSelectToBeCreated].some(
          (pps) =>
            get({ postProductSelect: [pps] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_TYPE(0)) ===
              ENTITY_TYPE.APPLICANT &&
            get({ postProductSelect: [pps] }, FIELD_PATHS.POST_PRODUCT_SELECT_ENTITY_ID(0)) === applicantId
        );

        if (!hasApplicantPPS) {
          postProductSelectToBeCreated.push(INITIAL_APPLICANT_POST_PRODUCT_SELECT(applicantId));
        }
      }
    });

  set(formValues, FIELD_PATHS.RELATED_PARTIES(), [...relatedParties, ...relatedPartiesToBeCreated]);
  set(formValues, FIELD_PATHS.POST_PRODUCT_SELECTS(), [...postProductSelects, ...postProductSelectToBeCreated]);
}
